import { lazy, Suspense, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { supabase } from '@/src/lib/supabase';
import { useAppColors } from '@/src/lib/theme';
import NumbersPanel from './numbers-panel';
import ForecastModal from './forecast-modal';

const InvoicesPage = lazy(() => import('@/src/components/invoices/invoices-page'));

export type FinTab = 'overview' | 'invoices' | 'forecast';

// Financials: the venue's numbers, invoices and forecast in one place.
// Owners and managers see all three; a head chef / bar manager (invoice access only) sees Invoices.
export default function FinancialsPage({ venueId, userId, userRole, canInvoices, tab, onTab }: {
  venueId: string; userId: string; userRole: string; canInvoices: boolean; tab: FinTab; onTab: (t: FinTab) => void;
}) {
  const c = useAppColors();
  const mgr = userRole === 'owner' || userRole === 'manager';
  const tabs: Array<{ id: FinTab; label: string }> = [
    ...(mgr ? [{ id: 'overview' as const, label: 'Overview' }] : []),
    ...(canInvoices ? [{ id: 'invoices' as const, label: 'Invoices' }] : []),
    ...(mgr ? [{ id: 'forecast' as const, label: 'Forecast' }] : []),
  ];
  const cur = tabs.some((t) => t.id === tab) ? tab : tabs[0]?.id ?? 'invoices';
  // Invoices waiting to be checked, shown on the tab.
  const [toCheck, setToCheck] = useState(0);
  useEffect(() => {
    if (!canInvoices) return;
    void supabase.from('invoices').select('id', { count: 'exact', head: true }).eq('venue_id', venueId).in('status', ['review', 'failed'])
      .then(({ count }) => setToCheck(count ?? 0));
  }, [venueId, canInvoices, cur]);

  return (
    <div className="h-full flex flex-col">
      <div className="max-w-6xl w-full mx-auto px-4 sm:px-8 pt-8">
        <h1 className="text-3xl font-semibold" style={{ color: c.text }}>Financials</h1>
        <p className="text-sm mt-1" style={{ color: c.textDim }}>{mgr ? 'Sales, costs, invoices and forecast.' : 'Supplier invoices and food / drinks cost.'}</p>
        {tabs.length > 1 && (
          <div className="inline-flex rounded-xl p-1 mt-5" style={{ background: c.iconBg }}>
            {tabs.map((t) => (
              <button key={t.id} onClick={() => onTab(t.id)} className="px-4 h-9 rounded-lg text-[13px] font-semibold flex items-center gap-1.5"
                style={cur === t.id ? { background: c.bg, color: c.text, boxShadow: '0 1px 2px rgba(0,0,0,0.08)' } : { color: c.textDim }}>
                {t.label}{t.id === 'invoices' && toCheck > 0 && <span style={{ color: c.gold }}>{toCheck}</span>}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="flex-1">
        {cur === 'overview' && <NumbersPanel venueId={venueId} userRole={userRole} userId={userId} embedded />}
        {cur === 'invoices' && <Suspense fallback={<div className="flex justify-center py-16"><Loader2 size={20} className="animate-spin" style={{ color: c.textFaint }} /></div>}><InvoicesPage venueId={venueId} userId={userId} userRole={userRole} embedded /></Suspense>}
        {cur === 'forecast' && <div className="max-w-6xl mx-auto px-4 sm:px-8 py-6"><ForecastModal inline isOpen onClose={() => onTab('overview')} venueId={venueId} userId={userId} /></div>}
      </div>
    </div>
  );
}
